import React, {
	createRef,
	Component,
	type ComponentType,
	type ComponentPropsWithoutRef,
} from 'react';
import { injectIntl } from '@atlassian/jira-intl';
import { colors, TimelineSkeleton } from '@atlassian/jira-portfolio-3-common';
import {
	type ListRowProps,
	List,
	WindowScroller,
} from '@atlassian/jira-portfolio-3-common/src/react-virtualized';
import {
	HorizontalScrollAnalytics,
	HorizontalScrollView,
} from '@atlassian/jira-portfolio-3-horizontal-scrolling';
import type { HorizontalScrollViewComponentType } from '@atlassian/jira-portfolio-3-horizontal-scrolling/src/ui/scroll-view';
import { INLINE_CREATE_ID } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/inline-create';
import type { ScopeIssue } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/scope/types';
import {
	TABLE_ISSUE,
	TABLE_INLINE_CREATE,
	TABLE_HEADER,
	TABLE_GROUP,
	TABLE_GROUP_HEADER,
	TABLE_BLANK_ROW,
	type TableItem,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/query/table';
import type { Mode } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/domain/app/types';
import StickyHeader from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/sticky-header';
import NowLine from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/timeline/now';
import ReleaseBar from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/timeline/release-bar';
import TimelineReleaseMarker from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/timeline/release-bar/timeline-marker';
import { DndItemProvider } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/timeline/schedule/dependency-lines/dnd-item';
import { getRowHighlighter } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/roadmap/util';
import { withSlots, slots } from '@atlassian/jira-portfolio-3-portfolio/src/common/component-slots';
import { injectIntlWithDefaultProps } from '@atlassian/jira-portfolio-3-portfolio/src/common/inject-intl-with-default-props';
import {
	SCOPE_ROW_HEIGHT,
	STICKY_HEADER_CONTAINER_PREFIX,
	DEPENDENCY_SETTINGS,
} from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import type { Warning } from '@atlassian/jira-portfolio-3-portfolio/src/common/warning-details/types';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';

import Arrows from './arrows';
import type { ArrowComponentType } from './arrows/types';

import {
	DependencyLinesProvider,
	DependencyLinesLayer,
	DependencyLinesLineDetail,
	type DependencyLinesLayerComponentType,
	type DependencyLinesProviderComponentType,
} from './dependency-lines';
import {
	DepLineDragPreviewLayer,
	DepLineDragPreviewProvider,
} from './dependency-lines/drag-preview';
import type { DepLineDragPreviewProviderComponentType } from './dependency-lines/drag-preview/provider/types';
import type { DependencyLinesLineDetailComponentType } from './dependency-lines/line-detail/types';
import { DndScrollingObserver } from './dnd-scrolling';
import Row, { Container as RowContainer } from './row';
import StickyTableGroup from './sticky-table-group';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import TableGroup from './table-group';
import type { Props, State } from './types';

export const STRIPED_BACKGROUND_COLOR = colors.N20_TRANSPARENT;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
export const IssueContainerSpacer = () => <div className={styles.containerSpacer} />;

// eslint-disable-next-line jira/react/no-class-components
class ScheduleComponent extends Component<Props, State> {
	// eslint-disable-next-line react/sort-comp
	static defaultProps: {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		Row: ComponentType<any>;
		NowLine: ComponentType<
			JSX.LibraryManagedAttributes<typeof NowLine, ComponentPropsWithoutRef<typeof NowLine>>
		>;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		StickyHeader: ComponentType<any>;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		TimelineReleaseMarker: ComponentType<any>;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		ReleaseBar: ComponentType<any>;
		DependencyLinesLayer: DependencyLinesLayerComponentType;
		DependencyLinesProvider: DependencyLinesProviderComponentType;
		DepLineDragPreviewProvider: DepLineDragPreviewProviderComponentType;
		DependencyLinesLineDetail: DependencyLinesLineDetailComponentType;
		HorizontalScrollView: HorizontalScrollViewComponentType;
		Arrows: ArrowComponentType;
	} = {
		Row,
		TimelineReleaseMarker,
		ReleaseBar,
		StickyHeader,
		DependencyLinesLayer,
		DependencyLinesProvider,
		NowLine,
		DepLineDragPreviewProvider,
		DependencyLinesLineDetail,
		HorizontalScrollView,
		Arrows,
	};

	static displayName = 'Schedule';

	// eslint-disable-next-line react/sort-comp
	static whyDidYouRender = true;

	state = {
		startIndex: 0,
		stopIndex: Infinity,
		overscanStartIndex: 0,
		overscanStopIndex: Infinity,
		linesDisabled: false,
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	ref = createRef<any>();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	listRef = createRef<any>();

	componentDidMount() {
		this.forceHandleScroll();
	}

	componentDidUpdate(prevProps: Props) {
		if (
			this.props.scrollElement !== prevProps.scrollElement ||
			// When zoomLevel changes, the DOM nodes may be shuffled
			this.props.zoomLevel !== prevProps.zoomLevel
		) {
			this.forceHandleScroll();
		}
		const listRef = this.listRef.current;
		if (listRef && prevProps.scope !== this.props.scope) {
			const length = Math.min(prevProps.scope.length, this.props.scope.length);
			for (let i = 0; i < length; i++) {
				if (prevProps.scope[i].height !== this.props.scope[i].height) {
					listRef.recomputeRowHeights();
					break;
				}
			}
		}

		// hide the dependency lines if we're dragging sections
		if (this.props.dependencySettingsDisplay === DEPENDENCY_SETTINGS.LINES) {
			if (!prevProps.isDragging && this.props.isDragging) {
				this.setState({ linesDisabled: true });
			}

			if (prevProps.isDragging && !this.props.isDragging) {
				this.setState({ linesDisabled: false });
			}
		}
	}

	forceHandleScroll() {
		// JPOS-2155 Timeline is not rendered until next scrolling event
		// After collapse/expand timeline item position is wrong because starting index for second part of table starts from 0
		// this workaround is for recalculating items position after colapse/expand timeline.
		const ref = this.ref.current;
		if (ref) {
			ref.__handleWindowScrollEvent();
			ref.__resetIsScrolling();
			/* eslint-enable */
		}
	}

	disableOtherRows = (issueId: string) => {
		this.props.enableOnlyIssueId(issueId);
		if (this.props.dependencySettingsDisplay === DEPENDENCY_SETTINGS.LINES) {
			this.setState({ linesDisabled: true });
		}
	};

	reEnableOtherRows = () => {
		this.props.enableOnlyIssueId();
		if (this.props.dependencySettingsDisplay === DEPENDENCY_SETTINGS.LINES) {
			this.setState({ linesDisabled: false });
		}
	};

	getWarningsForAnIssue = (issueId: string): Warning[] =>
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
		(this.props.warnings.byIssue[issueId] as any) || [];

	createRowForIssue = (issue: ScopeIssue, index: number, topOffset: number) => {
		// eslint-disable-next-line @typescript-eslint/no-shadow
		const { Row, onlyEnabledIssueId, width } = this.props;
		const isEven = issue.rootIndex % 2 === 0;
		const backgroundColor = isEven ? undefined : STRIPED_BACKGROUND_COLOR;
		const isReadOnly =
			(onlyEnabledIssueId && issue.id !== onlyEnabledIssueId) || issue.id === INLINE_CREATE_ID;

		const rowItem = (
			<Row
				backgroundColor={backgroundColor}
				disableOtherRows={this.disableOtherRows}
				isReadOnly={isReadOnly}
				issue={issue}
				issueWarnings={this.getWarningsForAnIssue(issue.id || '')}
				key={issue.id}
				reEnableOtherRows={this.reEnableOtherRows}
				topOffset={topOffset}
				width={width}
			/>
		);

		return (
			<DndItemProvider scopeIndex={index} issueId={issue.id}>
				{rowItem}
			</DndItemProvider>
		);
	};

	rowRenderer = ({ key, index, style }: ListRowProps) => {
		const { scope, showWarning, width } = this.props;

		const item: TableItem = scope[index];
		const { group } = item;
		const { tag } = item;

		switch (tag) {
			case TABLE_ISSUE: {
				const issue: ScopeIssue = item.value;
				// remove when cleaning ff arj-remove-row-warning-highlighting_jqjs5
				const issueWarnings: Warning[] = this.getWarningsForAnIssue(issue.id || '');

				// remove when cleaning ff arj-remove-row-warning-highlighting_jqjs5
				const hasWarnings = showWarning && issueWarnings.length > 0;
				return (
					<div
						key={`${item.tag}-${group || ''}-${issue.id}`}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={styles.row}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={{ ...style }}
					>
						<RowContainer
							// remove 'highlighter' prop when cleaning ff arj-remove-row-warning-highlighting_jqjs5
							highlighter={getRowHighlighter(issue.group, issue.id, hasWarnings)}
						>
							{this.createRowForIssue(issue, index, item.topOffset)}
						</RowContainer>
					</div>
				);
			}
			case TABLE_INLINE_CREATE: {
				const issue: ScopeIssue = item.value;
				return (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div key={`${item.tag}-${group || ''}-${issue.id}`} style={style}>
						<RowContainer>{this.createRowForIssue(issue, index, item.topOffset)}</RowContainer>
					</div>
				);
			}
			case TABLE_HEADER: {
				return (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div key={key} style={style}>
						<RowContainer />
					</div>
				);
			}
			case TABLE_GROUP_HEADER: {
				return (
					<div
						key={key}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						style={style}
						data-testid={`portfolio-3-portfolio.app-simple-plans.main.tabs.roadmap.timeline.schedule.table-header-group.${item.value.id}`}
					>
						<RowContainer>
							<IssueContainerSpacer />
						</RowContainer>
					</div>
				);
			}
			case TABLE_BLANK_ROW: {
				return (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div key={key} style={style}>
						<RowContainer>
							{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
							<div style={{ height: 40 }} />
						</RowContainer>
					</div>
				);
			}

			case TABLE_GROUP: {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				return <TableGroup width={width} item={item} style={style} key={key} />;
			}
			default:
				throw new Error(`Unexpected TABLE item variant: ${tag}`);
		}
	};

	rowHeight = (index: number) => (this.props.scope[index]?.height ?? 1) * SCOPE_ROW_HEIGHT;

	getContent = () => {
		const {
			scope,
			scrollElement,
			onlyEnabledIssueId,
			timelineRange,
			showCapacity,
			width,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			TimelineReleaseMarker,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			ReleaseBar,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			NowLine,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			StickyHeader,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			DependencyLinesLayer,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			DependencyLinesProvider,
			leftSidebarWidth,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			DepLineDragPreviewProvider,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			DependencyLinesLineDetail,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			HorizontalScrollView,
			// eslint-disable-next-line @typescript-eslint/no-shadow
			Arrows,
		} = this.props;

		const { startIndex, stopIndex, overscanStartIndex, overscanStopIndex, linesDisabled } =
			this.state;

		// NOTE rows={scope} is passed to List to trigger re-render when items content was changed
		// but length remained the same
		// & timelineRange is passed to List to trigger re-render of sprint lines when the timeline
		// is changed
		let ret = scrollElement ? (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			<div className={styles.schedule}>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={styles['release-grid']}>
					<ReleaseBar
						width={width}
						renderMarker={({
							date,
							mode,
							versionIdsMapByDate,
						}: {
							date: string;
							mode: Mode;
							versionIdsMapByDate: { [key: string]: string[] };
						}) => (
							<TimelineReleaseMarker
								key={date}
								endDate={date}
								mode={mode}
								versionIdsMapByDate={versionIdsMapByDate}
							/>
						)}
					/>
				</div>
				<WindowScroller scrollElement={scrollElement} ref={this.ref}>
					{({ height = 0, isScrolling, onChildScroll, scrollTop }) => (
						// eslint-disable-next-line jira/react/no-empty-divs
						<div>
							<StickyHeader containerId={`${STICKY_HEADER_CONTAINER_PREFIX}-timeline`}>
								<StickyTableGroup width={width} />
							</StickyHeader>
							<List
								ref={this.listRef}
								autoHeight
								height={height}
								isScrolling={isScrolling}
								onScroll={onChildScroll}
								onlyEnabledIssueId={onlyEnabledIssueId}
								rowCount={scope.length}
								rowHeight={({ index }) => {
									// eslint-disable-next-line @typescript-eslint/no-shadow
									const height = scope[index].height || 1;
									return height * SCOPE_ROW_HEIGHT;
								}}
								rowRenderer={this.rowRenderer}
								onRowsRendered={({
									// eslint-disable-next-line @typescript-eslint/no-shadow
									startIndex,
									// eslint-disable-next-line @typescript-eslint/no-shadow
									stopIndex,
									// eslint-disable-next-line @typescript-eslint/no-shadow
									overscanStartIndex,
									// eslint-disable-next-line @typescript-eslint/no-shadow
									overscanStopIndex,
								}) => {
									this.setState({
										startIndex,
										stopIndex,
										overscanStartIndex,
										overscanStopIndex,
									});
								}}
								rows={scope}
								scrollTop={scrollTop}
								tabIndex={null}
								width={width}
								state={this.state}
								timelineRange={timelineRange}
								showCapacity={showCapacity}
								overscanRowCount={20}
							/>
							{!linesDisabled && (
								<DependencyLinesLayer
									scope={scope}
									startIndex={startIndex}
									stopIndex={stopIndex}
									timelineRange={timelineRange}
									width={width}
								/>
							)}
							<DepLineDragPreviewLayer
								scope={scope}
								scrollTop={scrollTop}
								timelineRange={timelineRange}
								width={width}
							/>
							<DndScrollingObserver
								scrollElement={scrollElement}
								leftSidebarWidth={leftSidebarWidth}
							/>
							<ContextualAnalyticsData attributes={{ rowCount: scope.length }}>
								<HorizontalScrollAnalytics />
							</ContextualAnalyticsData>
							<NowLine noMarker />
						</div>
					)}
				</WindowScroller>
			</div>
		) : null;

		ret = (
			<>
				<HorizontalScrollView>{ret}</HorizontalScrollView>
				<Arrows
					containerWidth={width}
					scope={scope}
					rowHeight={this.rowHeight}
					verticalRenderingWindow={{
						overscanStartIndex,
						overscanStopIndex,
					}}
				/>
			</>
		);

		ret = (
			<DependencyLinesProvider>
				<DepLineDragPreviewProvider width={width}>{ret}</DepLineDragPreviewProvider>
				<DependencyLinesLineDetail scope={scope} />
			</DependencyLinesProvider>
		);

		return ret;
	};

	render() {
		const { intl, isLoading } = this.props;
		return isLoading ? (
			<TimelineSkeleton ariaLabel={intl.formatMessage(commonMessages.loadingIssuesMessage)} />
		) : (
			this.getContent()
		);
	}
}

export const Schedule = injectIntlWithDefaultProps(ScheduleComponent);

export default injectIntl(withSlots({ NowLine: slots.NowLine })(ScheduleComponent));
